import { __assign } from "tslib";
import React from 'react';
import { noopLogger } from 'typescript-log';
import { useLocation } from 'react-router';
export var PagePropertiesContext = React.createContext(undefined);
export var PageLifecycleProvider = function (_a) {
    var children = _a.children, onEvent = _a.onEvent, _b = _a.logger, logger = _b === void 0 ? noopLogger() : _b;
    // This will work assuming this component updates before children using the same hook
    var location = useLocation();
    var locationRef = React.useRef(location);
    locationRef.current = location;
    var previousLocation = usePrevious(location);
    // This needs to be outside of React's lifecycle so it's immediately consistent
    var loadingDataCount = React.useRef(0);
    // Set to true when location changes, set to false again when update is complete
    var isRouting = React.useRef(true);
    // This needs to be outside React's lifecycle because we want these props to be immediately
    // consistent as it can be updated through context, then we raise events using the latest value
    // If it was stored in React state, it would be out of date
    var contextValue = React.useMemo(function () { return ({
        currentPageProps: [],
        beginLoadingData: beginLoadingData,
        endLoadingData: endLoadingData,
        currentPageLocation: location,
    }); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // While routing our state may be inconsistent
    if (previousLocation && !isRouting.current) {
        // We only care about pathname, not any of the other location info
        if (previousLocation !== location) {
            if (logger) {
                logger.debug({
                    oldPath: previousLocation.pathname,
                    newPath: location.pathname,
                }, 'Path changed');
            }
            isRouting.current = true;
        }
    }
    React.useEffect(function () {
        if (!isRouting.current) {
            return;
        }
        // No data load triggered
        if (loadingDataCount.current === 0) {
            raisePageLoadStartEvent(logger, onEvent, contextValue.currentPageProps, location);
            raisePageLoadCompleteEvent(logger, onEvent, contextValue.currentPageProps, location);
            isRouting.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, isRouting.current]);
    function beginLoadingData() {
        if (loadingDataCount.current === 0 && isRouting.current) {
            raisePageLoadStartEvent(logger, onEvent, contextValue.currentPageProps, location);
        }
        loadingDataCount.current++;
        logger.debug({ loadingDataCount: loadingDataCount.current }, 'Begin loading data');
    }
    function endLoadingData() {
        loadingDataCount.current--;
        logger.debug({ loadingDataCount: loadingDataCount.current }, 'End loading data');
        // If pages load data after the page has transitioned to complete, just ignore it
        if (loadingDataCount.current === 0 && isRouting.current) {
            isRouting.current = false;
            raisePageLoadCompleteEvent(logger, onEvent, contextValue.currentPageProps, locationRef.current);
        }
    }
    if (typeof children === 'function') {
        return (React.createElement(PagePropertiesContext.Provider, { value: contextValue }, children({
            beginLoadingData: beginLoadingData,
            endLoadingData: endLoadingData,
        })));
    }
    return (React.createElement(PagePropertiesContext.Provider, { value: contextValue }, React.Children.only(children)));
};
PageLifecycleProvider.displayName = 'PageLifecycleProvider';
function usePrevious(value) {
    // The ref object is a generic container whose current property is mutable ...
    // ... and can hold any value, similar to an instance property on a class
    var ref = React.useRef();
    // Store current value in ref
    React.useEffect(function () {
        ref.current = value;
    }, [value]); // Only re-run if value changes
    // Return previous value (happens before update in useEffect above)
    return ref.current;
}
function raisePageLoadStartEvent(logger, onEvent, currentPageProps, location) {
    logger.debug({}, 'Rasing start load event');
    onEvent({
        type: 'page-load-started',
        originator: 'PageEvents',
        payload: __assign(__assign({}, currentPageProps.reduce(function (acc, val) { return (__assign(__assign({}, acc), val.current)); }, {})), { location: location }),
        timeStamp: new Date().getTime(),
    });
}
function raisePageLoadCompleteEvent(logger, onEvent, currentPageProps, location) {
    var reducedProps = currentPageProps.reduce(function (acc, val) { return (__assign(__assign({}, acc), val.current)); }, {});
    logger.debug({ currentPageProps: reducedProps }, 'Raising page load complete event');
    onEvent({
        type: 'page-load-complete',
        originator: 'PageEvents',
        payload: __assign(__assign({}, reducedProps), { location: location }),
        timeStamp: new Date().getTime(),
    });
}
//# sourceMappingURL=PageLifecycleProvider.js.map